import React, { useEffect, useMemo } from "react";
import _ from "lodash";
import cx from 'classnames';

import { Media } from "../../types/media";
import { IKImage, IKVideo } from "imagekitio-react";

import styles from './thumbnail.module.scss';

interface ThumbnailProps {
  media: Media | null | undefined;
  alt?: string;
  defaultImage?: string;
  className?: string;
  width?: number;
  height?: number;
  position?: string;
  crop?: {
    x: number;
    y: number;
    width: number;
    height: number;
  }
}

const Thumbnail: React.FC<ThumbnailProps> = ({ media, defaultImage, className, position, width, height, alt, crop }) => {
  const imageKey = _.get(media, 'key');
  const fallbackImage = useMemo(() => {
    const fullUrl = _.get(media, 'full');
    if (fullUrl) {
      return fullUrl;
    }

    return defaultImage;
  }, [media, defaultImage]);

  const type = _.get(media, 'type');

  if (imageKey) {
    if (type === 'video') {
      return <VideoView url={imageKey} 
        className={className} 
        width={width} 
        height={height} 
        position={position} 
      />
    } else if (type === 'image') {
      return <ImageView media={media} 
        className={className} 
        width={width} 
        height={height} 
        crop={crop} 
        position={position} 
      />
    }
  }

  if (type === 'video') {
    return <UrlVideoView url={fallbackImage || ''} className={className} position={position} />
  }

  return <UrlImageView 
    url={fallbackImage || ''} 
    className={className} 
    crop={crop}
    position={position}
    alt={alt} 
  />
}

interface ImageViewProps {
  media: Media | null | undefined;
  className?: string;
  width?: number;
  height?: number;
  position?: string;
  crop?: {
    x: number;
    y: number;
    width: number;
    height: number;
  }
}

const ImageView: React.FC<ImageViewProps> = ({ media, className, position, crop, width, height }) => {
  const url = media?.key;
  const transformations: object[] = useMemo(() => {
    const transformation: any[] = [];
    if (crop) {
      transformation.push({
        width: crop.width,
        height: crop.height,
        cropMode: 'extract',
        x: crop.x,
        y: crop.y
      });
    }

    const dimensionTransformation = {};
    if(width) {
      dimensionTransformation['width'] = width;
    }

    if(height) {
      dimensionTransformation['height'] = height;
    }

    if(!_.isEmpty(dimensionTransformation)) {
      transformation.push(dimensionTransformation);
    }

    transformation.push({
      quality: 85,
      pr: true,
      format: 'webp'
    });


    return transformation;
  }, [width, height, crop, media]);

  const backgroundStyles = useMemo(() => {
    if (position) {
      return {
        objectPosition: position,
      }
    }

    return {};
  }, [position]);

  if (!url) {
    return null;
  }

  const key = JSON.stringify(transformations);

  return (
    <IKImage 
      className={cx(styles.thumbnail, className, 'thumbnail_image')}
      key={key}
      path={url}
      transformation={transformations}
      style={backgroundStyles}
    />
  );
}

interface VideoViewProps {
  url: string | undefined;
  className?: string;
  width?: number;
  height?: number;
  position?: string;
}

const VideoView: React.FC<VideoViewProps> = ({ url, className, width, height, position }) => {
  const transformations: object[] = useMemo(() => {
    return [{
      width: width,
      height: height,
      quality: 95,
    }];
  }, [width, height]);

  const backgroundStyles = useMemo(() => {
    if (position) {
      return {
        objectPosition: position,
      }
    }

    return {};
  }, [position]);

  if (!url) {
    return null;
  }

  return (
    <IKVideo className={cx(styles.thumbnail, className)}
      path={url}
      transformation={transformations}
      controls={false}
      autoPlay={true}
      muted={true}
      loop={true}
      style={backgroundStyles}
    />
  );
}

interface UrlImageViewProps {
  url: string;
  alt?: string;
  className?: string;
  position?: string;
  crop?: {
    x: number;
    y: number;
    width: number;
    height: number;
  }
}

const UrlImageView: React.FC<UrlImageViewProps> = ({ url, alt, className, position, crop }) => {
  const imageRef = React.useRef<HTMLImageElement>(null);

  const loadImage = () => {
    if(imageRef.current === null) {
      return;
    }
    
    const image = new Image();
    image.crossOrigin="anonymous";
    image.src = url;

    image.onload = () => {
      if (crop) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        canvas.width = crop.width;
        canvas.height = crop.height;

        ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);

        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        if(imageRef.current){
          imageRef.current.src = dataUrl;
        }
        
      }else {
        if(imageRef.current){
          imageRef.current.src = url;
        }
      }
    }

    return image;
  };

  useEffect(() => {
    loadImage();
  }, [url, crop]);

  const backgroundStyles = useMemo(() => {
    if (position) {
      return {
        objectPosition: position,
      }
    }

    return {};
  }, [position]);

  return <img ref={imageRef} className={cx(styles.thumbnail, className)} alt={alt} style={backgroundStyles} />
}

interface UrlImageViewProps {
  url: string;
  className?: string;
  position?: string;
}

const UrlVideoView: React.FC<UrlImageViewProps> = ({ url, className, position}) => {
  const backgroundStyles = useMemo(() => {
    if (position) {
      return {
        objectPosition: position,
      }
    }

    return {};
  }, [position]);

  return <video src={url} className={cx(styles.thumbnail, className)} 
    controls={false} autoPlay loop muted 
    style={backgroundStyles}
  />
}

export default Thumbnail;